import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  message,
  Avatar,
  Spin,
  Popconfirm,
} from 'antd';
import {
  CrownOutlined,
  UserOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { vipAPI, usersAPI, getImageUrl } from '../services/api';

const { Title, Text } = Typography;

const VIPManagement = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [vipUsers, setVipUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, vipUsersRes, allUsersRes] = await Promise.all([
        vipAPI.getStats(),
        usersAPI.getAll({ role: 'vip', limit: 100 }),
        usersAPI.getAll({ role: 'user', limit: 100 })
      ]);
      
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (vipUsersRes.data.success) {
        setVipUsers(vipUsersRes.data.data.users || []);
      }
      if (allUsersRes.data.success) {
        setAllUsers(allUsersRes.data.data.users || []);
      }
    } catch (error) {
      message.error('获取数据失败');
      console.error('Fetch VIP data error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 升级用户为 VIP
  const handleUpgradeToVip = async (user) => {
    try {
      setSubmitting(true);
      await usersAPI.update(user.id, { role: 'vip' });
      message.success(`已将 ${user.username} 升级为 VIP 会员`);
      fetchData();
    } catch (error) {
      message.error('操作失败');
      console.error('Upgrade error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 取消用户 VIP
  const handleRevokeVip = async (user) => {
    try {
      setSubmitting(true);
      await usersAPI.update(user.id, { role: 'user' });
      message.success(`已将 ${user.username} 的 VIP 资格取消`);
      fetchData();
    } catch (error) {
      message.error('操作失败');
      console.error('Revoke error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // VIP 会员表格列
  const vipColumns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={getImageUrl(record.avatar)} />
          <div>
            <div><Text strong>{record.username}</Text></div>
            <small style={{ color: '#999' }}>{record.email}</small>
          </div>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: () => (
        <Tag color="gold" icon={<CrownOutlined />}>
          VIP 会员
        </Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="确定取消该用户的VIP资格吗？"
          onConfirm={() => handleRevokeVip(record)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<UserDeleteOutlined />}>
            取消VIP
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // 普通用户表格列
  const userColumns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={getImageUrl(record.avatar)} />
          <div>
            <div><Text strong>{record.username}</Text></div>
            <small style={{ color: '#999' }}>{record.email}</small>
          </div>
        </Space>
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: () => (
        <Tag color="default">普通用户</Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="确定将该用户升级为VIP会员吗？"
          onConfirm={() => handleUpgradeToVip(record)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" icon={<CrownOutlined />} style={{ color: '#faad14' }}>
            升级为VIP
          </Button>
        </Popconfirm>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Title level={4}>VIP 会员管理</Title>

      {/* Statistics */}
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="VIP 会员总数"
              value={stats.totalVipMembers || vipUsers.length || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="普通用户总数"
              value={stats.regularUsers || allUsers.length || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="本月新增 VIP"
              value={stats.newVipThisMonth || 0}
              prefix={<UserAddOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* VIP Users Table */}
      <Card 
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            VIP 会员列表
          </Space>
        }
      >
        <Table
          columns={vipColumns}
          dataSource={vipUsers}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: '暂无VIP会员' }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Regular Users Table */}
      <Card 
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            普通用户列表（可升级为VIP）
          </Space>
        }
      >
        <Table
          columns={userColumns}
          dataSource={allUsers}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: '暂无普通用户' }}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </Space>
  );
};

export default VIPManagement;
